import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30'
];

const monthLabel = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const ConsultationCalendar = () => {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(null); // Date object
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', purpose: '', notes: '' });
  const [selectedSlot, setSelectedSlot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();

  const grid = useMemo(() => {
    // Build a Mon..Sun grid for the current cursor month
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    }
    return cells;
  }, [cursor]);

  const openForDate = async (date) => {
    setSelectedDate(date);
    setDialogOpen(true);
    setSelectedSlot('');
    setSuccess(false);
    setLoadingSlots(true);
    try {
      const { data } = await api.get('/consultations/booked-slots', { params: { date: toISO(date) } });
      setBookedSlots(data.booked || []);
    } catch {
      setBookedSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) { toast({ title: 'Pick a time slot first' }); return; }
    if (!form.name || !form.email) { toast({ title: 'Name and email are required' }); return; }
    setSubmitting(true);
    try {
      await api.post('/consultations', {
        ...form,
        date: toISO(selectedDate),
        time_slot: selectedSlot,
      });
      setSuccess(true);
      toast({ title: 'Meeting requested!', description: 'We will confirm shortly.' });
      setForm({ name: '', email: '', phone: '', purpose: '', notes: '' });
    } catch (err) {
      toast({ title: 'Could not book', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const isPastMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0) < today;

  return (
    <div className="rounded-2xl p-5 bg-[#0b1220]/60 border border-white/10">
      <div className="flex items-center justify-between mb-3">
        <div className="text-slate-300 text-sm flex items-center gap-2">
          <CalendarDays size={14} className="text-amber-400" /> {monthLabel(cursor)}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            disabled={isPastMonth}
            className="h-7 w-7 rounded-md hover:bg-white/5 text-slate-400 disabled:opacity-30 flex items-center justify-center"
            aria-label="Previous month"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="h-7 w-7 rounded-md hover:bg-white/5 text-slate-400 flex items-center justify-center"
            aria-label="Next month"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5 text-center text-xs text-slate-500 mb-1">
        {[
          { key: 'mon', label: 'M' }, { key: 'tue', label: 'T' }, { key: 'wed', label: 'W' },
          { key: 'thu', label: 'T' }, { key: 'fri', label: 'F' }, { key: 'sat', label: 'S' },
          { key: 'sun', label: 'S' }
        ].map((d) => <div key={d.key}>{d.label}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-center">
        {grid.map((cell, i) => {
          if (!cell) return <div key={`blank-${cursor.getFullYear()}-${cursor.getMonth()}-${i}`} />;
          const isPast = cell < today;
          const isToday = cell.getTime() === today.getTime();
          const isSunday = cell.getDay() === 0;
          const disabled = isPast || isSunday;
          const cellKey = `${cell.getFullYear()}-${cell.getMonth()}-${cell.getDate()}`;
          let cellClass;
          if (disabled) {
            cellClass = 'text-slate-700 cursor-not-allowed';
          } else if (isToday) {
            cellClass = 'bg-amber-500/25 text-amber-200 border border-amber-500/50 hover:bg-amber-500/35';
          } else {
            cellClass = 'text-slate-300 bg-white/[0.02] hover:bg-amber-500/10 hover:text-amber-200 border border-transparent hover:border-amber-500/30';
          }
          return (
            <button
              key={cellKey}
              disabled={disabled}
              onClick={() => openForDate(cell)}
              className={`aspect-square rounded-md flex items-center justify-center text-[11px] transition-colors ${cellClass}`}
            >
              {cell.getDate()}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
        <span className="h-2 w-2 rounded-full bg-amber-400 inline-block"></span> Click a date to schedule a call
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0b1220] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-display text-xl">
              {success ? 'Request received' : 'Schedule a consultation'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedDate && !success && (
                <>Book a 30-minute session on <span className="text-amber-300">{selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span></>
              )}
              {success && 'We will confirm the meeting via email shortly.'}
            </DialogDescription>
          </DialogHeader>

          {success ? (
            <div className="py-6 text-center">
              <div className="h-14 w-14 mx-auto rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-4">
                <Check className="text-emerald-300" size={22} />
              </div>
              <p className="text-slate-300 text-sm">
                Your request has been sent. Our team will reach out on the provided contact to confirm the schedule.
              </p>
              <Button onClick={() => setDialogOpen(false)} className="mt-5 btn-gradient rounded-full h-10 px-6 border-0">Done</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs text-slate-400">Select time (30 min)</label>
                {loadingSlots ? (
                  <div className="text-slate-500 text-sm mt-3 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading availability...</div>
                ) : (
                  <div className="grid grid-cols-4 gap-1.5 mt-2">
                    {TIME_SLOTS.map((slot) => {
                      const booked = bookedSlots.includes(slot);
                      const active = selectedSlot === slot;
                      let slotClass;
                      if (booked) {
                        slotClass = 'text-slate-700 border-white/5 line-through cursor-not-allowed';
                      } else if (active) {
                        slotClass = 'bg-amber-500/20 text-amber-200 border-amber-500/50';
                      } else {
                        slotClass = 'text-slate-300 border-white/10 hover:bg-white/5 hover:border-amber-500/30';
                      }
                      return (
                        <button
                          type="button"
                          key={slot}
                          disabled={booked}
                          onClick={() => setSelectedSlot(slot)}
                          className={`text-xs h-9 rounded-md border transition-colors ${slotClass}`}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400">Full name *</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 bg-white/[0.03] border-white/10 text-white h-10" required />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Email *</label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1.5 bg-white/[0.03] border-white/10 text-white h-10" required />
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400">Phone (optional)</label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 ..." className="mt-1.5 bg-white/[0.03] border-white/10 text-white h-10" />
              </div>

              <div>
                <label className="text-xs text-slate-400">What would you like to discuss?</label>
                <Textarea value={form.notes} rows={3}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Brief context about your project or question..."
                  className="mt-1.5 bg-white/[0.03] border-white/10 text-white" />
              </div>

              <Button type="submit" disabled={submitting} className="w-full btn-gradient rounded-full h-11 border-0">
                {submitting ? 'Sending request...' : 'Request meeting'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsultationCalendar;
